import { useEffect, useState } from "react";
import { useArchiveSignalForAll, useClaimSignal, useDismissSignal } from "@/hooks/use-signal-queue-actions";
import { fetchFollowUpWithRelations, type FollowUpWithRelations } from "@/hooks/use-follow-ups";
import type { DBSignalQueueItem } from "@/lib/db-types";

/**
 * Handlingene på signalkortet i køen (VIB-2958). E-post, Ring og Følg opp senere
 * tar signalet (RPC claim_signal, den som er raskest får det) og gir tilbake
 * oppfølgingen RPC-en lagde, så kortet kan gå rett videre til e-post, samtale
 * eller utsett. `processId` er prosessen signalet legges i (null = ny prosess);
 * standard er kundens nyeste åpne prosess.
 */
export function useSignalCardFlow(signal: DBSignalQueueItem | null) {
  const claimMutation = useClaimSignal();
  const dismissMutation = useDismissSignal();
  const archive = useArchiveSignalForAll();
  const [processId, setProcessId] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);

  const signalId = signal?.id ?? null;
  const newestProcess = signal?.open_opportunities[0]?.id ?? null;
  useEffect(() => {
    setProcessId(newestProcess);
  }, [signalId, newestProcess]);

  const ids = signal ? { signalId: signal.id, customerId: signal.customer_id } : null;

  /** Tar signalet og henter oppfølgingen. Null når noen andre rakk det først eller noe feilet (hooken viser feilen). */
  const claim = async (): Promise<FollowUpWithRelations | null> => {
    if (!ids) return null;
    try {
      const result = await claimMutation.mutateAsync(
        processId ? { ...ids, mode: "attach", opportunityId: processId } : { ...ids, mode: "new" },
      );
      if (!result.follow_up_id) return null;
      setFetching(true);
      return await fetchFollowUpWithRelations(result.follow_up_id);
    } catch {
      return null;
    } finally {
      setFetching(false);
    }
  };

  return {
    pending: claimMutation.isPending || dismissMutation.isPending || archive.isPending || fetching,
    processId,
    setProcessId,
    claim,
    /** Avviser signalet. Løses til true når avvisningen er lagret, så en grunn kan settes etterpå (feil vises av hooken). */
    dismiss: async (): Promise<boolean> => {
      if (!ids) return false;
      try {
        await dismissMutation.mutateAsync(ids);
        return true;
      } catch {
        return false;
      }
    },
    archiveAll: () => ids && archive.mutate(ids),
  };
}
